<script setup lang="ts">
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import ProgressBar from 'primevue/progressbar';
import { computed } from 'vue';

// Inactivity warning. Shown by AppShell while the idle timer is inside the warning
// window. "Stay signed in" emits `stay` so the parent can reset the timer; in practice
// the act of moving the mouse to the button already resets it via the global activity
// listeners — the button is the explicit, obvious path.
const props = defineProps<{
  visible: boolean;
  seconds: number;
  warningSeconds: number;
}>();
const emit = defineEmits<{ stay: [] }>();

const pct = computed(() => {
  if (props.warningSeconds <= 0) return 0;
  return Math.max(0, Math.min(100, (props.seconds / props.warningSeconds) * 100));
});
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    :draggable="false"
    header="Still there?"
    :style="{ width: '26rem' }"
  >
    <div class="idle-body">
      <p>
        You'll be signed out in
        <strong>{{ seconds }}</strong>
        second{{ seconds === 1 ? '' : 's' }} due to inactivity.
      </p>
      <ProgressBar :value="pct" :show-value="false" class="idle-bar" />
      <p class="idle-hint">
        Click, scroll, or type to stay signed in (moving the mouse alone won't).
      </p>
    </div>

    <template #footer>
      <Button label="Stay signed in" icon="pi pi-check" @click="emit('stay')" />
    </template>
  </Dialog>
</template>

<style scoped>
.idle-body { display: flex; flex-direction: column; gap: 0.75rem; }
.idle-body p { margin: 0; }
.idle-bar { height: 0.5rem; }
.idle-hint { color: var(--p-text-muted-color, #6b7280); font-size: 0.8125rem; }
</style>
